/**
 * ====================================================================
 * FIKO CAD DIAMETER DIMENSION
 * ====================================================================
 *
 * Çap ölçüsü (⌀) - Daire, silindir veya yay için.
 * CATIA/IronCAD tarzında profesyonel çap ölçümü.
 *
 * Özellikler:
 * - Leader line (kılavuz çizgi)
 * - Çap sembolü (⌀)
 * - Merkez işareti
 * - İç veya dış yerleşim
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 */

import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { FikoSahne } from '../../FikoSahne';
import {
    Dimension,
    DimensionType,
    DimensionResult,
    DimensionState,
    GeometryReference,
    DiameterDimensionOptions,
    ArrowStyle,
    DIMENSION_CONSTANTS,
    DimensionErrorType
} from './DimensionTypes';

/**
 * Diameter Dimension - Çap ölçüsü
 */
export class DiameterDimension extends Dimension {
    // Referans
    private ref: GeometryReference;

    // Hesaplanmış değerler
    private center: THREE.Vector3 = new THREE.Vector3();
    private radius: number = 0;
    private normal: THREE.Vector3 = new THREE.Vector3(0, 0, 1);

    // Çizim elemanları
    private diameterLine: LineSegments2 | null = null;
    private leaderLine: Line2 | null = null;  // 🎯 Line2 for polyline (3+ points)
    private arrow1: THREE.Mesh | THREE.Line | null = null;
    private arrow2: THREE.Mesh | THREE.Line | null = null;

    // Shared geometries for arrow styles
    private static _sharedArrowGeometry: THREE.BufferGeometry | null = null;
    private static _sharedOpenArrowGeometry: THREE.BufferGeometry | null = null;
    private static _sharedDotGeometry: THREE.BufferGeometry | null = null;
    private static _sharedTickGeometry: THREE.BufferGeometry | null = null;
    private label: THREE.Sprite | null = null;
    private centerMark: THREE.Group | null = null;

    // Sahne referansı
    private scene: FikoSahne;

    // 🔴 SABİT BOYUT SİSTEMİ - FikoOlcumCizgisi ile aynı pattern
    sabitBoyut = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegistiThrottled();

    // 🚀 PERFORMANS: Smooth scale interpolation için
    private lastKameraUpdateTime: number = 0;
    private kameraUpdateTimeout: number | null = null;
    private static readonly KAMERA_THROTTLE_MS: number = 100;
    private targetScaleAdjustment: number = 1;
    private currentScaleAdjustment: number = 1;
    private interpolationRAF: number | null = null;
    private isDisposed = false;

    constructor(
        ref: GeometryReference,
        options: DiameterDimensionOptions,
        scene: FikoSahne
    ) {
        super(DimensionType.DIAMETER, { ...options, prefix: options.prefix || '⌀' });

        this.ref = ref;
        this.scene = scene;
    }

    /**
     * Dimension'ı oluştur
     */
    create(): DimensionResult {
        try {
            // Merkez ve yarıçap al
            if (!this.ref.center || !this.ref.radius) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Merkez veya yarıçap tanımlı değil'
                    }
                };
            }

            this.center.copy(this.ref.center);
            this.radius = this.ref.radius;

            if (this.ref.axis) {
                this.normal.copy(this.ref.axis);
            }

            // Değeri hesapla (çap = 2 × yarıçap)
            this._value = this.calculateValue();

            // Validate
            if (!this.validate()) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Çap çok küçük'
                    }
                };
            }

            // Geometriyi oluştur
            this.buildGeometry();

            // 🎯 FikoOlcumCizgisi ile uyumlu: Sahneye hazırla (event listener bağla)
            this.hazirla();

            return {
                success: true,
                dimension: this
            };
        } catch (error) {
            return {
                success: false,
                error: {
                    type: DimensionErrorType.INTERNAL_ERROR,
                    message: `Diameter dimension oluşturma hatası: ${error}`
                }
            };
        }
    }

    /**
     * Dimension'ı güncelle
     * 🎯 FikoOlcumCizgisi ile uyumlu: Sadece yazı sabit boyut, oklar ve çizgiler normal
     */
    update(): void {
        // 🎯 FikoOlcumCizgisi ile uyumlu: Oklar sabit boyutta kalır
        // Scale güncelleme event listener üzerinden yapılır (kameraYonDegisti)
    }

    /**
     * ✅ Sahneye hazırlama - FikoOlcumCizgisi ile aynı pattern
     * 🎯 v3.2.7: LinearDimension ile aynı - İlk scale'i HEMEN uygula
     */
    hazirla(): void {
        // console.log('[DiameterDimension] hazirla() çağrıldı', {
        //     scene: !!this.scene,
        //     anaGosterim: !!this.scene?.anaGosterim,
        //     aktifKamera: !!this.scene?.anaGosterim?.aktifKamera,
        //     label: !!this.label,
        //     originalScale: !!this.label?.userData?.originalScale
        // });
        if (!this.scene || !this.scene.anaGosterim || !this.scene.anaGosterim.aktifKamera) {
            // console.log('[DiameterDimension] hazirla() erken return - eksik referans');
            return;
        }

        try {
            // 🎯 v3.2.7: İlk scale'i HEMEN uygula (LinearDimension ile aynı)
            if (this.sabitBoyut && this.label && this.label.userData.originalScale) {
                const kamera = this.scene.anaGosterim.aktifKamera as any;
                const zoom = kamera?.zoom ?? 1;
                const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;
                const scaleAdjustment = factor * 0.02;

                // İlk scale'i LABEL'a uygula
                const originalScale = this.label.userData.originalScale as THREE.Vector3;
                this.label.scale.set(
                    originalScale.x * scaleAdjustment,
                    originalScale.y * scaleAdjustment,
                    1
                );
                this.currentScaleAdjustment = scaleAdjustment;
                this.targetScaleAdjustment = scaleAdjustment;
            }

            // 🔴 Kamera değiştiğinde - EVENT LİSTENER (FikoOlcumCizgisi pattern'i)
            if (!this.hazirlaListenerEklendi && this.scene.anaGosterim.aktifKontrol) {
                this.scene.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            // Hata durumunda sessizce devam et
        }
    }

    /**
     * 🚀 PERFORMANS: Throttled kamera event handler
     */
    private kameraYonDegistiThrottled(): void {
        const now = performance.now();

        if (this.kameraUpdateTimeout !== null) {
            return;
        }

        if (now - this.lastKameraUpdateTime < DiameterDimension.KAMERA_THROTTLE_MS) {
            this.kameraUpdateTimeout = window.setTimeout(() => {
                this.kameraUpdateTimeout = null;
                this.lastKameraUpdateTime = performance.now();
                this.kameraYonDegisti();
            }, DiameterDimension.KAMERA_THROTTLE_MS - (now - this.lastKameraUpdateTime));
            return;
        }

        this.lastKameraUpdateTime = now;
        this.kameraYonDegisti();
    }

    /**
     * 🔴 YAZININ BOYUTU SABİT - Çizgi normal kalır
     * 🎯 v3.2.7: LinearDimension ile aynı - Scale HEMEN uygula
     */
    private kameraYonDegisti(): void {
        if (!this.scene || !this.scene.anaGosterim || !this.scene.anaGosterim.aktifKamera) {
            return;
        }

        if (this.sabitBoyut && this.label && this.label.userData.originalScale) {
            const kamera = this.scene.anaGosterim.aktifKamera as any;
            const zoom = kamera?.zoom ?? 1;
            const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;
            const scaleAdjustment = factor * 0.02;

            // 🎯 v3.2.7: HEMEN uygula (smooth interpolation yerine)
            const originalScale = this.label.userData.originalScale as THREE.Vector3;
            this.label.scale.set(
                originalScale.x * scaleAdjustment,
                originalScale.y * scaleAdjustment,
                1
            );
            this.currentScaleAdjustment = scaleAdjustment;
            this.targetScaleAdjustment = scaleAdjustment;
        }
    }

    /**
     * 🎯 SMOOTH: Her frame interpolation
     */
    private startSmoothInterpolation(): void {
        if (this.interpolationRAF !== null) return;

        const interpolate = () => {
            if (!this.label || !this.label.userData.originalScale || !this.sabitBoyut) {
                this.interpolationRAF = null;
                return;
            }

            const lerpFactor = 0.2;
            const diff = this.targetScaleAdjustment - this.currentScaleAdjustment;

            if (Math.abs(diff) < 0.001) {
                this.currentScaleAdjustment = this.targetScaleAdjustment;
                this.interpolationRAF = null;
                return;
            }

            this.currentScaleAdjustment += diff * lerpFactor;

            const originalScale = this.label.userData.originalScale as THREE.Vector3;
            this.label.scale.set(
                originalScale.x * this.currentScaleAdjustment,
                originalScale.y * this.currentScaleAdjustment,
                1
            );

            this.interpolationRAF = requestAnimationFrame(interpolate);
        };

        this.interpolationRAF = requestAnimationFrame(interpolate);
    }

    /**
     * Validate
     */
    validate(): boolean {
        return this._value >= DIMENSION_CONSTANTS.MIN_RADIUS * 2;
    }

    /**
     * Değeri hesapla
     */
    protected calculateValue(): number {
        return this.radius * 2;
    }

    /**
     * Geometriyi oluştur
     */
    protected buildGeometry(): void {
        this.clearGeometry();

        const options = this._options as DiameterDimensionOptions;
        const color = options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        const lineWidth = options.lineWidth || DIMENSION_CONSTANTS.DEFAULT_LINE_WIDTH;
        // 🎯 FikoOlcumCizgisi ile uyumlu: Varsayılan olarak merkez geçen çap çizgisi
        const placement = options.placement || 'inside';

        // Çap yönünü hesapla (kameraya göre)
        const diameterDir = this.calculateDiameterDirection();

        // Çap çizgisi uç noktaları
        const point1 = this.center.clone().addScaledVector(diameterDir, this.radius);
        const point2 = this.center.clone().addScaledVector(diameterDir, -this.radius);

        if (placement === 'inside') {
            // İçerde: merkez geçen çap çizgisi
            this.buildInsidePlacement(point1, point2, diameterDir, color, lineWidth);
        } else {
            // Dışarıda: leader line ile
            this.buildOutsidePlacement(point1, point2, diameterDir, color, lineWidth);
        }

        // Merkez işareti
        if (options.showCenterMark !== false) {
            this.centerMark = this.createCenterMark(color, options.centerMarkSize || 2);
            this.add(this.centerMark);
        }

        // 🎯 v3.2.7: Label oluşturulduktan sonra HEMEN scale uygula (LinearDimension ile aynı)
        if (this.sabitBoyut && this.label && this.label.userData.originalScale && this.scene?.anaGosterim?.aktifKamera) {
            const kamera = this.scene.anaGosterim.aktifKamera as any;
            const zoom = kamera?.zoom ?? 1;
            const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;
            const scaleAdjustment = factor * 0.02;

            const originalScale = this.label.userData.originalScale as THREE.Vector3;
            this.label.scale.set(
                originalScale.x * scaleAdjustment,
                originalScale.y * scaleAdjustment,
                1
            );
            this.currentScaleAdjustment = scaleAdjustment;
            this.targetScaleAdjustment = scaleAdjustment;
        }
    }

    /**
     * İç yerleşim (çap çizgisi merkezden geçer)
     * 🎯 FikoOlcumCizgisi capCizgiOlustur ile AYNI pattern
     */
    private buildInsidePlacement(
        point1: THREE.Vector3,
        point2: THREE.Vector3,
        direction: THREE.Vector3,
        color: number,
        lineWidth: number
    ): void {
        const cap = this._value; // Çap değeri
        const yariCap = this.radius;

        // 🎯 FikoOlcumCizgisi: Dinamik ok boyutları (çapa göre)
        const okRadius = Math.max(0.15, Math.min(0.4, cap * 0.02));
        const okHeight = Math.max(0.4, Math.min(1.0, cap * 0.05));
        const arrowSize = this._options.arrowSize || Math.max(okRadius, okHeight);

        // 🎯 ÇAP ÇİZGİSİ: İki uçta solid filled oklar ile profesyonel görünüm
        this.diameterLine = this.createLine(
            [point1.x, point1.y, point1.z, point2.x, point2.y, point2.z],
            color,
            lineWidth
        );
        this.diameterLine.name = 'DiameterLine';
        this.add(this.diameterLine);

        // 🎯 BI-DIRECTIONAL ARROWHEADS: Oklar merkeze doğru (içeri) bakıyor
        // CAD standardı: Çap ölçümünde oklar içeri, mesafe ölçümünde oklar dışarı
        const capYonu = direction.clone().normalize();

        // Ok1: point1'de, merkeze doğru bakıyor (point2 yönüne = capYonu tersi)
        const ok1Yonu = capYonu.clone().multiplyScalar(-1);
        this.arrow1 = this.createArrow(point1, ok1Yonu, color, arrowSize);
        this.arrow1.name = 'Arrow1';
        this.add(this.arrow1);

        // Ok2: point2'de, merkeze doğru bakıyor (point1 yönüne = capYonu)
        const ok2Yonu = capYonu.clone();
        this.arrow2 = this.createArrow(point2, ok2Yonu, color, arrowSize);
        this.arrow2.name = 'Arrow2';
        this.add(this.arrow2);

        // 🎯 LEADER LINE: Çap yazısı için kırılmalı leader (FikoOlcumCizgisi ile aynı)
        // Yazı merkeze yakın konumlandırılır, leader ile bağlanır
        const leaderBaslangic = this.center.clone();
        leaderBaslangic.y += yariCap * 0.3; // Yukarı

        const leaderBitis = leaderBaslangic.clone();
        leaderBitis.x += cap * 0.15; // Sağa yatay

        this.leaderLine = this.createPolyline(
            [
                this.center.x, this.center.y, this.center.z,
                leaderBaslangic.x, leaderBaslangic.y, leaderBaslangic.z,
                leaderBitis.x, leaderBitis.y, leaderBitis.z
            ],
            color,
            lineWidth * 0.6,
            0.7 // opacity
        );
        this.leaderLine.name = 'DiameterLeader';
        this.add(this.leaderLine);

        // Label (leader ucunda)
        this.label = this.createLabel(this.formatValue(this._value), leaderBitis);
        this.label.name = 'Label';
        this.add(this.label);
    }

    /**
     * Dış yerleşim (leader line ile)
     */
    private buildOutsidePlacement(
        point1: THREE.Vector3,
        point2: THREE.Vector3,
        direction: THREE.Vector3,
        color: number,
        lineWidth: number
    ): void {
        const options = this._options as DiameterDimensionOptions;
        const arrowSize = options.arrowSize || DIMENSION_CONSTANTS.DEFAULT_ARROW_SIZE;
        const leaderAngle = (options.leaderAngle || 45) * Math.PI / 180;
        const leaderLength = options.leaderLength || this.radius * 1.5;

        // Leader başlangıç noktası (daire üzerinde)
        const leaderStart = point1.clone();

        // Leader yönü
        const leaderDir = new THREE.Vector3();
        leaderDir.copy(direction);

        // Açı uygula
        const rotAxis = this.normal.clone();
        const rotQuat = new THREE.Quaternion().setFromAxisAngle(rotAxis, leaderAngle);
        leaderDir.applyQuaternion(rotQuat);

        // Leader bitiş noktası
        const leaderEnd = leaderStart.clone().addScaledVector(leaderDir, leaderLength);

        // Leader çizgisi (Line2 for consistency)
        this.leaderLine = this.createPolyline(
            [leaderStart.x, leaderStart.y, leaderStart.z, leaderEnd.x, leaderEnd.y, leaderEnd.z],
            color,
            lineWidth
        );
        this.leaderLine.name = 'LeaderLine';
        this.add(this.leaderLine);

        // Ok (daire üzerinde, içe doğru)
        this.arrow1 = this.createArrow(leaderStart, direction.clone().negate(), color, arrowSize);
        this.arrow1.name = 'Arrow1';
        this.add(this.arrow1);

        // Label (leader ucunda)
        this.label = this.createLabel(this.formatValue(this._value), leaderEnd);
        this.label.name = 'Label';
        this.add(this.label);
    }

    /**
     * Çap yönünü hesapla
     * 🎯 FikoOlcumCizgisi uyumluluğu: Önce referanstan gelen yönü kullan, yoksa kameraya göre hesapla
     */
    private calculateDiameterDirection(): THREE.Vector3 {
        // 🎯 FikoOlcumCizgisi uyumluluğu: Referanstan gelen yön varsa onu kullan
        // (capUc1, capUc2 verilmişse direction hesaplanmış olur)
        if (this.ref.direction && this.ref.direction.lengthSq() > 0.01) {
            return this.ref.direction.clone().normalize();
        }

        // Kameraya göre hesaplama (fallback)
        if (this.scene?.anaGosterim?.aktifKamera) {
            const camera = this.scene.anaGosterim.aktifKamera;

            // Kameradan merkeze yön
            const toCamera = new THREE.Vector3()
                .subVectors(camera.position, this.center)
                .normalize();

            // Normal'a dik yön (daire düzleminde)
            const diameterDir = new THREE.Vector3()
                .crossVectors(this.normal, toCamera)
                .normalize();

            if (diameterDir.lengthSq() < 0.01) {
                // Kamera normal'a paralelse, alternatif yön
                diameterDir.crossVectors(this.normal, new THREE.Vector3(0, 1, 0)).normalize();
            }

            return diameterDir;
        }

        // Fallback
        return new THREE.Vector3(1, 0, 0);
    }

    /**
     * Merkez işareti oluştur
     * 🎯 Normal vektörüne göre düzeltilmiş - 3D ortamda doğru pozisyon
     */
    private createCenterMark(color: number, size: number): THREE.Group {
        const group = new THREE.Group();
        group.name = 'CenterMark';

        // 🎯 Normal vektörüne dik iki yön hesapla
        let dir1 = new THREE.Vector3(1, 0, 0);
        if (Math.abs(this.normal.dot(dir1)) > 0.9) {
            dir1.set(0, 1, 0);
        }
        dir1.crossVectors(this.normal, dir1).normalize();
        const dir2 = new THREE.Vector3().crossVectors(this.normal, dir1).normalize();

        // Yatay çizgi (normal'a dik)
        const h1 = this.center.clone().addScaledVector(dir1, -size);
        const h2 = this.center.clone().addScaledVector(dir1, size);

        const hLine = this.createLine(
            [h1.x, h1.y, h1.z, h2.x, h2.y, h2.z],
            color,
            0.5
        );
        group.add(hLine);

        // Dikey çizgi (normal'a dik, dir1'e dik)
        const v1 = this.center.clone().addScaledVector(dir2, -size);
        const v2 = this.center.clone().addScaledVector(dir2, size);

        const vLine = this.createLine(
            [v1.x, v1.y, v1.z, v2.x, v2.y, v2.z],
            color,
            0.5
        );
        group.add(vLine);

        return group;
    }

    /**
     * Çizgi oluştur
     */
    private createLine(
        positions: number[],
        color: number,
        lineWidth: number,
        opacity: number = 1
    ): LineSegments2 {
        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color,
            linewidth: lineWidth,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity,
            fog: false,
            worldUnits: false,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        const line = new LineSegments2(geometry, material);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    /**
     * 🎯 Polyline oluştur (3+ nokta için Line2 kullan)
     * FikoOlcumCizgisi leader line ile aynı pattern
     */
    private createPolyline(
        positions: number[],
        color: number,
        lineWidth: number,
        opacity: number = 1
    ): Line2 {
        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color,
            linewidth: lineWidth,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity,
            fog: false,
            worldUnits: false
        });

        const line = new Line2(geometry, material);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    /**
     * Ok oluştur - ArrowStyle enum desteği
     */
    private createArrow(
        position: THREE.Vector3,
        direction: THREE.Vector3,
        color: number,
        size: number
    ): THREE.Mesh | THREE.Line {
        const arrowStyle = this._options.arrowStyle || ArrowStyle.FILLED;

        // NONE style - görünmez placeholder
        if (arrowStyle === ArrowStyle.NONE) {
            const empty = new THREE.Mesh();
            empty.visible = false;
            return empty;
        }

        const material = new THREE.MeshBasicMaterial({
            color,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        let geometry: THREE.BufferGeometry;

        switch (arrowStyle) {
            case ArrowStyle.OPEN:
                // Açık ok - V şeklinde (CATIA tarzı geniş ok)
                if (!DiameterDimension._sharedOpenArrowGeometry) {
                    const shape = new THREE.Shape();
                    shape.moveTo(-0.35, -0.5);
                    shape.lineTo(0, 0.5);
                    shape.lineTo(0.35, -0.5);
                    DiameterDimension._sharedOpenArrowGeometry = new THREE.ShapeGeometry(shape);
                    DiameterDimension._sharedOpenArrowGeometry.rotateX(Math.PI / 2);
                }
                geometry = DiameterDimension._sharedOpenArrowGeometry;
                break;

            case ArrowStyle.DOT:
                // Nokta (görünür boyutta)
                if (!DiameterDimension._sharedDotGeometry) {
                    DiameterDimension._sharedDotGeometry = new THREE.SphereGeometry(0.25, 12, 12);
                }
                geometry = DiameterDimension._sharedDotGeometry;
                break;

            case ArrowStyle.TICK:
                // Çizgi (architectural tick) - 45 derece çapraz çizgi, kalın
                if (!DiameterDimension._sharedTickGeometry) {
                    const tickShape = new THREE.Shape();
                    tickShape.moveTo(-0.35, -0.35);
                    tickShape.lineTo(0.35, 0.35);
                    tickShape.lineTo(0.42, 0.28);
                    tickShape.lineTo(-0.28, -0.42);
                    tickShape.closePath();
                    DiameterDimension._sharedTickGeometry = new THREE.ShapeGeometry(tickShape);
                    DiameterDimension._sharedTickGeometry.rotateX(Math.PI / 2);
                }
                geometry = DiameterDimension._sharedTickGeometry;
                break;

            case ArrowStyle.FILLED:
            default:
                // Dolu ok (CAD standardı) - Geniş ve uzun profesyonel ok
                // 🎯 FikoOlcumCizgisi ile uyumlu: Pivot tabanında, ok ucu pozisyon noktasında
                if (!DiameterDimension._sharedArrowGeometry) {
                    DiameterDimension._sharedArrowGeometry = new THREE.ConeGeometry(0.3, 0.8, 12);
                    // Pivot noktasını cone'nin tabanına taşı (ok ucu pozisyonda olacak)
                    DiameterDimension._sharedArrowGeometry.translate(0, -0.4, 0); // height/2 = 0.8/2 = 0.4
                }
                geometry = DiameterDimension._sharedArrowGeometry;
                break;
        }

        const arrow = new THREE.Mesh(geometry, material);
        arrow.position.copy(position);
        arrow.scale.setScalar(size);

        // 🎯 FikoOlcumCizgisi ile AYNI: Y ekseni yukarı varsayılan, quaternion OKA UYGULANIYOR
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
        arrow.quaternion.copy(quaternion);  // ✅ KRITIK: Quaternion'u oka uygula!

        arrow.renderOrder = Infinity;
        arrow.frustumCulled = false;

        return arrow;
    }

    /**
     * Label oluştur
     * 🎯 CETVEL İLE AYNI: Siyah arka plan + beyaz yazı
     */
    private createLabel(text: string, position: THREE.Vector3): THREE.Sprite {
        const options = this._options;
        const fontSize = options.fontSize || DIMENSION_CONSTANTS.DEFAULT_FONT_SIZE;
        // 🎯 CETVEL İLE AYNI: Beyaz yazı (options'tan veya varsayılan)
        const textColor = options.textColor || '#ffffff';
        // 🎯 CETVEL İLE AYNI: Siyah arka plan
        const backgroundColor = (options as any).backgroundColor || '#000000';
        const backgroundOpacity = (options as any).backgroundOpacity ?? 1;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = fontSize * 4;

        const padding = 16;
        canvas.width = textWidth + padding * 2;
        canvas.height = textHeight + padding * 2;

        // 🎯 CETVEL İLE AYNI: Siyah arka plan çiz
        ctx.fillStyle = backgroundColor;
        ctx.globalAlpha = backgroundOpacity;
        const borderRadius = 6;
        this.roundRect(ctx, 0, 0, canvas.width, canvas.height, borderRadius);
        ctx.fill();
        ctx.globalAlpha = 1;

        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // 🎯 CETVEL İLE AYNI: Beyaz yazı (stroke kaldırıldı)
        ctx.fillStyle = textColor;
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            sizeAttenuation: true,
            toneMapped: false
        });

        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.copy(position);
        // 🎯 Kompakt yazı scale'i - küçük ve okunabilir
        sprite.scale.set(canvas.width * 0.015, canvas.height * 0.015, 1);
        sprite.renderOrder = Infinity;
        sprite.frustumCulled = false;

        // 🎯 FikoOlcumCizgisi ile uyumlu: Original scale'i sakla (sabit boyut için)
        sprite.userData.originalScale = new THREE.Vector3().copy(sprite.scale);

        return sprite;
    }

    /**
     * Görünümü güncelle
     */
    protected updateAppearance(): void {
        let color: number;

        switch (this._state) {
            case DimensionState.HOVERED:
                color = DIMENSION_CONSTANTS.COLORS.HOVERED;
                break;
            case DimensionState.SELECTED:
                color = DIMENSION_CONSTANTS.COLORS.SELECTED;
                break;
            default:
                color = this._options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        }

        [this.diameterLine, this.leaderLine].forEach(line => {
            if (line) {
                (line.material as LineMaterial).color.setHex(color);
            }
        });

        [this.arrow1, this.arrow2].forEach(arrow => {
            if (arrow) {
                (arrow.material as THREE.MeshBasicMaterial).color.setHex(color);
            }
        });

        if (this.centerMark) {
            this.centerMark.traverse(child => {
                if (child instanceof LineSegments2) {
                    (child.material as LineMaterial).color.setHex(color);
                }
            });
        }
    }

    /**
     * Geometriyi temizle
     */
    private clearGeometry(): void {
        [this.diameterLine, this.leaderLine].forEach(line => {
            if (line) {
                this.remove(line);
                line.geometry.dispose();
                (line.material as LineMaterial).dispose();
            }
        });

        [this.arrow1, this.arrow2].forEach(arrow => {
            if (arrow) {
                this.remove(arrow);
                arrow.geometry.dispose();
                (arrow.material as THREE.Material).dispose();
            }
        });

        if (this.label) {
            this.remove(this.label);
            this.label.material.map?.dispose();
            this.label.material.dispose();
        }

        if (this.centerMark) {
            this.centerMark.traverse(child => {
                if (child instanceof LineSegments2) {
                    child.geometry.dispose();
                    (child.material as LineMaterial).dispose();
                }
            });
            this.remove(this.centerMark);
        }

        this.diameterLine = null;
        this.leaderLine = null;
        this.arrow1 = null;
        this.arrow2 = null;
        this.label = null;
        this.centerMark = null;
    }

    /**
     * Dispose - 🎯 FikoOlcumCizgisi ile uyumlu kaynak temizleme
     */
    override dispose(): void {
        if (this.isDisposed) return;
        this.isDisposed = true;

        if (this.kameraUpdateTimeout !== null) {
            clearTimeout(this.kameraUpdateTimeout);
            this.kameraUpdateTimeout = null;
        }

        if (this.interpolationRAF !== null) {
            cancelAnimationFrame(this.interpolationRAF);
            this.interpolationRAF = null;
        }

        if (this.scene?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.scene.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        this.clearGeometry();
        super.dispose();
    }

    /**
     * 🎯 CETVEL İLE AYNI: Yuvarlatılmış köşeli dikdörtgen çiz
     */
    private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
    }
}
